import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Settings2 } from 'lucide-react';

const IssueTypeConfig = ({ issueType, onUpdate, isDemoMode }) => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({
    required: issueType.attachment_config?.required || false,
    visible: issueType.attachment_config?.visible || false,
    accept_types: issueType.attachment_config?.accept_types || 'image/*,video/*',
    max_size_mb: issueType.attachment_config?.max_size_mb || 50,
    instruction: issueType.attachment_config?.instruction || '',
  });

  const handleSave = () => {
    onUpdate(issueType.issue_type_id, {
      name: issueType.name,
      attachment_config: config.visible ? config : null
    });
    setOpen(false);
  };

  const acceptTypeOptions = [
    { value: 'image/*', label: 'Images Only' },
    { value: 'video/*', label: 'Videos Only' },
    { value: 'image/*,video/*', label: 'Images & Videos' },
    { value: '.pdf', label: 'PDF Only' },
    { value: 'image/*,.pdf', label: 'Images & PDF' },
    { value: '*', label: 'All Files' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" disabled={isDemoMode} title={isDemoMode ? 'Not available in demo' : undefined} data-testid={`config-issue-type-${issueType.issue_type_id}`}>
          <Settings2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading">Configure: {issueType.name}</DialogTitle>
          <DialogDescription>Set attachment requirements for this issue type</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Show Attachment Field</Label>
              <p className="text-sm text-muted-foreground">Display attachment upload for this issue type</p>
            </div>
            <Switch
              checked={config.visible}
              onCheckedChange={(checked) => setConfig({ ...config, visible: checked })}
              data-testid="toggle-visible"
            />
          </div>

          {config.visible && (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Make Required</Label>
                  <p className="text-sm text-muted-foreground">Users must upload attachment to submit ticket</p>
                </div>
                <Switch
                  checked={config.required}
                  onCheckedChange={(checked) => setConfig({ ...config, required: checked })}
                  data-testid="toggle-required"
                />
              </div>

              <div>
                <Label htmlFor="accept-types">Accepted File Types</Label>
                <Select value={config.accept_types} onValueChange={(value) => setConfig({ ...config, accept_types: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="accept-types-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {acceptTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="max-size">Maximum File Size (MB)</Label>
                <Input
                  id="max-size"
                  type="number"
                  min="1"
                  max="500"
                  value={config.max_size_mb}
                  onChange={(e) => setConfig({ ...config, max_size_mb: parseInt(e.target.value) })}
                  className="mt-1.5"
                  data-testid="max-size-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Recommended: 10MB for images, 200MB for videos
                </p>
              </div>

              <div>
                <Label htmlFor="instruction">Instructions for Customer</Label>
                <Textarea
                  id="instruction"
                  placeholder="e.g., Please attach a clear photo of the damaged product"
                  value={config.instruction}
                  onChange={(e) => setConfig({ ...config, instruction: e.target.value })}
                  rows={3}
                  className="mt-1.5"
                  data-testid="instruction-textarea"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This message will be displayed below the attachment field
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isDemoMode} title={isDemoMode ? 'Not available in demo' : undefined} data-testid="save-config-btn">
              Save Configuration
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IssueTypeConfig;
